import { CheckIcon } from '@radix-ui/react-icons'
import { useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export type MultiSelectOption = {
  value: string
  label: string
}

type MultiSelectFilterProps = {
  label: string
  icon?: ReactNode
  options: MultiSelectOption[]
  selected: string[]
  onChange: (next: string[]) => void
  searchPlaceholder?: string
  emptyText?: string
  className?: string
}

export function MultiSelectFilter({
  label,
  icon,
  options,
  selected,
  onChange,
  searchPlaceholder = 'Search…',
  emptyText = 'No results found.',
  className,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false)

  const selectAll = () => onChange(options.map((option) => option.value))
  const deselectAll = () => onChange([])

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value])
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={className ?? 'w-full justify-start text-left font-normal sm:w-auto'}
        >
          {icon}
          <span className="mr-2">{label}</span>
          <span className="bg-primary/10 ml-auto rounded-full px-1.5 py-0.5 text-xs font-medium">{selected.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[240px]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={selectAll} className="cursor-pointer">
                <span>Select All</span>
                {selected.length === options.length && options.length > 0 ? (
                  <CheckIcon className="ml-auto size-4" />
                ) : null}
              </CommandItem>
              <CommandItem onSelect={deselectAll} className="cursor-pointer">
                <span>Deselect All</span>
                {selected.length === 0 ? <CheckIcon className="ml-auto size-4" /> : null}
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => toggle(option.value)}
                  className="cursor-pointer"
                >
                  <span className="truncate">{option.label}</span>
                  {selected.includes(option.value) ? <CheckIcon className="ml-auto size-4 shrink-0" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
